import { isRunningStatus } from '@agilelab/plugin-wb-builder-common';
import {
  customAlertApiRef,
  WbButton,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import { useEditorPageContext } from '../context/useEditorPageContext';
import { builderDpPoliciesTest } from '@agilelab/plugin-wb-rbac-common';
import { stringifyEntityRef } from '@mainstage/catalog-model';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { usePermission } from '@mainstage/plugin-permission-react';
import { ButtonProps } from '@material-ui/core';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import { FC, useCallback, useEffect } from 'react';
import { panelCatalogApiRef } from '../../../api';
import { generateTestTooltip } from '../../common/utils';

type Props = ButtonProps & { label?: string };

interface DescriptorStateWithComponentsRef {
  loading: boolean;
  value: string | null | undefined;
}

let descriptorStateWithComponentsRef:
  | DescriptorStateWithComponentsRef
  | undefined;

export const LaunchTestButton: FC<Props> = props => {
  const {
    descriptorState,
    descriptorStateWithComponents,
    fetchLastTest,
    parent,
    entity,
    setSelectedTestTab,
    setShowCurrentTest,
    fetchTests,
    setIsValidatingDescriptor,
    lastTestState,
    setTestLoading,
    testLoading,
    actionError,
  } = useEditorPageContext();

  const panelCatalog = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const configApi = useApi(configApiRef);
  const useAsyncValidation = configApi.getOptionalBoolean(
    'catalog.enableAsyncValidation',
  );

  const project = parent ?? entity;

  useEffect(() => {
    descriptorStateWithComponentsRef = {
      loading: descriptorStateWithComponents.loading,
      value: descriptorStateWithComponents.value,
    };
  }, [descriptorStateWithComponents]);

  const waitForDescriptorToLoad = async () => {
    while (descriptorStateWithComponentsRef?.loading) {
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
  };

  const launchPoliciesTests = useCallback(async () => {
    setTestLoading(true);
    setIsValidatingDescriptor(true);

    setSelectedTestTab('current');
    setShowCurrentTest(true);

    await waitForDescriptorToLoad();

    if (project) {
      try {
        if (useAsyncValidation) {
          await panelCatalog.validateDescriptorAsync(
            descriptorStateWithComponentsRef?.value || '',
            project?.metadata.name,
            project?.kind,
          );
          fetchLastTest();
          return;
        }

        await panelCatalog.validateDescriptor(
          descriptorStateWithComponentsRef?.value || '',
          project?.metadata.name,
          project?.kind,
        );
        fetchTests();
        fetchLastTest();

        setTestLoading(false);
        setIsValidatingDescriptor(false);
      } catch (error) {
        alertApi.post({
          error,
          severity: 'error',
        });
        setTestLoading(false);
        setIsValidatingDescriptor(false);
      }
    }
  }, [
    setTestLoading,
    setSelectedTestTab,
    setShowCurrentTest,
    project,
    alertApi,
    useAsyncValidation,
    setIsValidatingDescriptor,
    panelCatalog,
    fetchTests,
    fetchLastTest,
  ]);

  /**
   * this is only used when useAsyncValidation is true, it is used
   * in order to re-enable the test button when the async validation test finishes
   */
  useEffect(() => {
    if (useAsyncValidation && !lastTestState.loading && lastTestState.value) {
      if (
        isRunningStatus(lastTestState.value.status) ||
        lastTestState.value?.tasks.some(task => isRunningStatus(task.status))
      )
        return;
      setTestLoading(false);
    }
  }, [lastTestState, useAsyncValidation, setTestLoading]);

  const { allowed: canLaunchTest } = usePermission({
    permission: builderDpPoliciesTest,
    resourceRef: stringifyEntityRef({
      kind: entity?.kind ?? 'system',
      namespace: 'default',
      name: entity?.metadata.name ?? '',
    }),
  });

  return (
    <WbTooltip
      title={generateTestTooltip(
        canLaunchTest,
        actionError,
        descriptorState.loading,
      )}
    >
      <span>
        <WbButton
          icon={<PlayCircleFilledIcon />}
          label={props.label || 'Test'}
          onClick={launchPoliciesTests}
          loading={testLoading}
          disabled={testLoading || !canLaunchTest || !!actionError}
          {...props}
        />
      </span>
    </WbTooltip>
  );
};
